import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../context/context";
import UiButton1 from "../../components/ui_button1";
import csv from "csvtojson"
import UiSpinner from "../../components/ui_spinner";

const EditorImport = () => {

    const {
        neonUser,
        setToastContent,
        toastInit,
        offCanvas,
        addslashes,
        brand,
        setReloadProductTable,
        qouteDelimiter,
        fetchDataV2
    } = useContext(AppContext)

    const [loaded, setLoaded] = useState(false)
    const [columns, setColumns] = useState([])
    const [fileData, setFileData] = useState(null)
    const [selectedFile, setSelectedFile] = useState(null)
    const [error, setError] = useState("")
    const [tooltip, setTooltip] = useState(null)

    const elFile = useRef(null)

    const validateAndWrite = async () => {
        setLoaded(false)
        setError("")

        if (!selectedFile) {
            setLoaded(true)
            setError("No file to upload.")
            return
        }

        let valid = true
        let compare = Object.keys(fileData[0]).map(xx => xx.replace(/\s+/g, ' ').trim())
        columns.forEach(x => {
            if (!compare.includes(x)) valid = false
        })

        if (!valid) {
            setLoaded(true)
            setError("Required columns are missing in file provided.")
            return
        }

        let writeData = fileData.filter(x => !!x["Product ID"])
        writeData = writeData.filter(x => x["Client ID"] == brand.brand_entity_id && x['Agency ID'] == brand.agency_id)

        let edit_entries = []

        try {
            let [resp, resp2] = await Promise.all([
                fetchDataV2("GET_EXISTING_EDITS", {
                    product_ids: writeData.map(x => x["Product ID"])
                }, "/bigquery-sql-v2"),
                fetchDataV2("GET_VALID_ASINS", {
                    agency_id: brand.agency_id,
                    brand_entity_id: brand.brand_entity_id,
                    asins: writeData.map(x => x["Child ASIN"])
                }, "/bigquery-sql-v2")
            ])

            resp = resp.map(x => x.product_id).join(", ")
            resp2 = resp2.map(x => x['Child ASIN']).join(", ")

            edit_entries = writeData.filter(x => resp.includes(x["Product ID"]) && resp2.includes(x["Child ASIN"]))

            if (edit_entries.length > 0) {
                await fetchDataV2("DELETE_EXISTING_EDITS", {
                    product_ids: edit_entries.map(x => x["Product ID"])
                }, "/bigquery-sql-v2")
            }
        }
        catch (err) {
            return
        }

        let limit = 100

        const chunkInsert = async (data) => {
            try {
                await fetchDataV2("CHUNK_INSERT", {
                    rows: data.map(x => ({
                        "Product ID": x["Product ID"],
                        json_data: addslashes(JSON.stringify(x))
                    })),
                    auth0_user_id: neonUser.user.id
                }, "/bigquery-sql-v2")
                return true
            }
            catch (err) {
                return false
            }
        }

        if (writeData.length < 100) {
            await chunkInsert(writeData)
        }
        else {
            for (let i = 0; i < writeData.length; i += limit) {
                const chunk = writeData.slice(i, i + limit);
                await new Promise(resolve => {
                    setTimeout(() => {
                        chunkInsert(chunk)
                        resolve(true)
                    }, 200)
                })
            }
        }

        setLoaded(true)
        setToastContent("Data successfully uploaded")
        toastInit.show()
        offCanvas.hide()
        setSelectedFile(null)
        setReloadProductTable(true)
        return
    }

    const getColumns = async () => {
        return setColumns([
            `Country Code`, `Client ID`, `Agency ID`, `Parent ASIN`, `Child ASIN`, `Product ID`
        ])
    }

    const fileHandler = () => {
        setError("")
        let file = elFile.current.files[0]
        if (!file) {
            setError("No file selected.")
            return
        }

        setSelectedFile(elFile.current.files[0])
        let read = new FileReader()

        read.onload = (e) => {
            let res = e.target.result
            try {
                csv({ output: "json" })
                    .fromString(res)
                    .then((res2) => {
                        let data = res2.map(x => ({
                            ...x,
                            "Product ID": qouteDelimiter(x["Product ID"], 2)
                        }))
                        setFileData(data)
                    })
            }
            catch (err) {
                return
            }
        }

        read.readAsText(file, "UTF-8");
    }

    const resetFile = () => {
        elFile.current.file = null
        document.getElementById("file").files = null
        setSelectedFile(null)
    }

    useEffect(() => {
        if (selectedFile) setError("")
    }, [selectedFile])

    useEffect(() => {
        if (neonUser && columns.length > 0) setLoaded(true)
    }, [neonUser, columns])

    useEffect(() => {
        if (loaded && !tooltip) {
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl))
            setTooltip(tooltipList)
        }
    }, [loaded])

    useEffect(() => {
        getColumns()
    }, [])

    if (!loaded) return (
        <div className="d-flex justify-content-center h-100">
            <UiSpinner />
        </div>
    )

    return (
        <>
            <div className="d-flex flex-column">
                <p className="fs-4 mb-0">Import</p>
                <div>
                    {error && <p className="text-danger poppins">{error}</p>}
                    <p className="poppins">
                        File must be a&nbsp;
                        <span className="fw-bold">.csv</span>
                        &nbsp;and include all required&nbsp;
                        <a className="fw-bold" data-bs-toggle="tooltip" data-bs-placement="bottom" data-bs-html="true" data-bs-title={`
                            <p>Required columns are:</p>
                            <ul>
                            ${columns.length > 0 && columns.map(x => `<li>${x}</li>`).join("")}
                            </ul>
                        `}>columns</a>.&nbsp;
                        Only ASINs matching the current Brand and existing in the product data will be imported.
                    </p>

                    {selectedFile ?
                        <div className="d-flex gap-2">
                            <p className="poppins">File:&nbsp;
                                <span className="fw-bold">{selectedFile.name}</span>
                            </p>
                            <i onClick={() => resetFile()} className="bi bi-x-lg" style={{ cursor: "pointer" }}></i>
                        </div>
                        :
                        <div onClick={() => elFile.current.click()} className="button3 py-2 px-3" style={{ display: "inline-block", cursor: "pointer" }}>
                            <i className="bi bi-file-earmark"></i> Select File
                        </div>
                    }

                    <input ref={elFile} hidden id="file" type="file" onChange={() => fileHandler()} accept=".csv" />
                </div>
                {selectedFile &&
                    <div>
                        <UiButton1 text="Submit" submit={validateAndWrite} />
                    </div>
                }
            </div>
        </>
    )
}

export default EditorImport